/**
 *
 * @module playbackSession
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/MediaApi.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/MediaApi.md#playback-scenarios-and-ad-insertion-strategy
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/playback-session.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/playhead.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Drm from './../drm/entry';
import Token from './../token/entry';
import AdEngineManager from './adEngine/adEngineManager';
import MediaItem from './mediaItem';
import MediaManager from './mediaManager';
import MediaAnalyticsKey from './mediaAnalyticsKey';
import PlaylistType from './playlistType';
import PlaybackTelemetryDispatcher from './playbackTelemetryDispatcher';
import AuthCookieProvider from './authCookieProvider';
import PlayerAdapter from './playerAdapter';
import AtvPlayerAdapter from './playerAdapter/atvPlayerAdapter';
import BamWebPlayerAdapter from './playerAdapter/bamWebPlayerAdapter';
import TrilithiumPlayerAdapter from './playerAdapter/trilithiumPlayerAdapter';
import ChromecastPlayerAdapter from './playerAdapter/chromecastPlayerAdapter';
import CafPlayerAdapter from './playerAdapter/cafPlayerAdapter';
import BamHlsPlayerAdapter from './playerAdapter/bamHlsPlayerAdapter';

const TokenManager = Token.TokenManager;
const DrmManager = Drm.DrmManager;

const WidevineDrmProvider = Drm.WidevineDrmProvider;
const AtvFairPlayDrmProvider = Drm.AtvFairPlayDrmProvider;
const FairPlayDrmProvider = Drm.FairPlayDrmProvider;
const PlayReadyDrmProvider = Drm.PlayReadyDrmProvider;
const SilkDrmProvider = Drm.SilkDrmProvider;

const Util = Services.Util;
const DrmType = Services.Media.DrmType;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const PlaybackSessionDustUrnReference = Services.Internal.Dust.DustUrnReference.media.playbackSession;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    includes,
    instanceStrict
} = Check.assert;

/**
 *
 * @class PlaybackSession
 * @since 2.0.0
 * @desc Encapsulates the playlist request and player adapter preparation.
 *
 */
export default class PlaybackSession {

    /**
     *
     * @param {Object} options
     * @param {SDK.Token.TokenManager} options.tokenManager
     * @param {SDK.Drm.DrmManager} options.drmManager
     * @param {MediaManager} options.mediaManager
     * @param {PlaybackTelemetryDispatcher} options.telemetryDispatcher
     * @param {SDK.Logging.Logger} options.logger
     * @param {PlayerAdapter} options.playerAdapter
     * @param {AuthCookieProvider} options.authCookieProvider
     * @param {SDK.Media.AdEngine.AdEngineManager} options.adEngineManager
     * @param {Array<SDK.Services.Media.DrmType>} [options.priorityDrms=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'PlaybackSession(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            },
            get drmManager() {
                return `${this.ctor} needs SDK.Drm.DrmManager instance`;
            },
            get mediaManager() {
                return `${this.ctor} needs SDK.Media.MediaManager instance`;
            },
            get telemetryDispatcher() {
                return `${this.ctor} needs PlaybackTelemetryDispatcher instance`;
            },
            get logger() {
                return `${this.ctor} needs SDK.Logger instance`;
            },
            get playerAdapter() {
                return `${this.ctor} needs PlayerAdapter instance`;
            },
            get authCookieProvider() {
                return `${this.ctor} needs AuthCookieProvider instance`;
            },
            get adEngineManager() {
                return `${this.ctor} needs SDK.Media.AdEngine.AdEngineManager`;
            },
            get priorityDrms() {
                return `${this.ctor} needs an array of SDK.Services.Media.DrmType`;
            }
        };

        const { tokenManager, drmManager, mediaManager, telemetryDispatcher, logger = Logging } = options;
        const { playerAdapter, authCookieProvider, adEngineManager, priorityDrms = null } = options;

        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);
        instanceStrict(drmManager, DrmManager, errorMessage.drmManager, InvalidArgumentException);
        instanceStrict(mediaManager, MediaManager, errorMessage.mediaManager, InvalidArgumentException);
        instanceStrict(telemetryDispatcher, PlaybackTelemetryDispatcher, errorMessage.telemetryDispatcher, InvalidArgumentException); // eslint-disable-line
        instanceStrict(logger, Logging.Logger, errorMessage.logger, InvalidArgumentException);
        instanceStrict(playerAdapter, PlayerAdapter, errorMessage.playerAdapter, InvalidArgumentException);
        instanceStrict(authCookieProvider, AuthCookieProvider, errorMessage.authCookieProvider, InvalidArgumentException); // eslint-disable-line
        instanceStrict(adEngineManager, AdEngineManager, errorMessage.adEngineManager, InvalidArgumentException);

        if (Check.assigned(priorityDrms)) {
            array.of.nonEmptyString(priorityDrms, errorMessage.priorityDrms, InvalidArgumentException);
        }

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Drm.DrmManager}
         *
         */
        this.drmManager = drmManager;

        /**
         *
         * @access private
         * @type {MediaManager}
         *
         */
        this.mediaManager = mediaManager;

        /**
         *
         * @access private
         * @type {PlaybackTelemetryDispatcher}
         *
         */
        this.telemetryDispatcher = telemetryDispatcher;

        /**
         *
         * @access private
         * @type {PlayerAdapter}
         *
         */
        this.playerAdapter = playerAdapter;

        /**
         *
         * @access private
         * @type {AuthCookieProvider}
         *
         */
        this.authCookieProvider = authCookieProvider;

        /**
         *
         * @access private
         * @type {SDK.Media.AdEngine.AdEngineManager}
         *
         */
        this.adEngineManager = adEngineManager;

        /**
         *
         * @access private
         * @type {Array<SDK.Services.Media.DrmType>}
         *
         */
        this.priorityDrms = priorityDrms;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.mediaManager.client.dustEnabled);

        /**
         *
         * @desc listen to {TokenManager} events
         *
         */
        this.bindTokenProviderEvents();

        this.logger.log(PlaybackSession.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {MediaItem} mediaItem
     * @param {PlaylistType} [playlistType=PlaylistType.COMPLETE] - Specifies playlist to
     * prepare (COMPLETE or SLIDE).
     * @desc Uses provided payload to prepare the player. Assigns to local payload variable.
     * @returns {Playlist} - containing information about the media that was prepared.
     * @note MediaItem#getPreferredPlaylist(playlistType). If not provided, uses MediaItem#getDefaultPlaylist().
     *
     */
    prepare(mediaItem, playlistType = PlaylistType.COMPLETE) {

        const errorMessage = {
            method: 'PlaybackSession.prepare(mediaItem, playlistType)',
            get mediaItem() {
                return `${this.method} needs MediaItem instance`;
            },
            get playlistType() {
                return `${this.method} needs a valid PlaylistType`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: PlaybackSession.name,
            urn: PlaybackSessionDustUrnReference.prepare,
            data: {
                streamUrl: mediaItem.completeStreamUri,
                playbackScenario: mediaItem.basePlaybackScenario
            }
        });

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);
        includes(PlaylistType, playlistType, errorMessage.playlistType, InvalidArgumentException);

        const { telemetryDispatcher, playerAdapter } = this;
        const { authCookieProvider, tokenManager } = this;
        const { playhead = {} } = mediaItem;
        const { position, status } = playhead || {};

        const accessToken = tokenManager.getAccessToken();

        logger.info(PlaybackSession.name, 'Execute #prepare');
        logger.info(PlaybackSession.name, `Playlist type: ${playlistType}`);

        if (Check.assigned(position)) {
            logger.info(PlaybackSession.name, `Playhead position: "${position}"`);
        }

        if (Check.assigned(status)) {
            logger.info(PlaybackSession.name, `Playhead status: "${status}"`);
        }

        let playlist;
        let playbackAttributes;

        return this.updatePlayerAdapter().then(() => {

            return mediaItem.getTrackingData(MediaAnalyticsKey.Telemetry);

        }).then((telemetryTrackingData) => {

            if (Check.assigned(telemetryTrackingData)) {
                telemetryDispatcher.init(playerAdapter, telemetryTrackingData);
            }

            return mediaItem.getPreferredPlaylist(playlistType);

        }).then((preferredPlaylist) => {

            playlist = preferredPlaylist;
            playbackAttributes = preferredPlaylist.attributes;

            return this.setDrm(playbackAttributes);

        }).then(() => {

            return this.updateAdEngineCookies(mediaItem);

        }).then(() => {

            return authCookieProvider.createCookie(accessToken);

        }).then(() => {

            logger.info(PlaybackSession.name, `Setting source on PlayerAdapter, ${playlist.streamUri}`);

            return playerAdapter.setSource(playlist.streamUri);

        }).then(() => {

            logger.info(PlaybackSession.name, 'Successfully set source on PlayerAdapter');

            return Promise.resolve(playlist);

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @desc Initiates an extra, immediate stream sample collection,
     * based on the player's current playhead.
     * @returns {Promise<Void>}
     *
     */
    collectStreamSample() {

        const { telemetryDispatcher, playerAdapter } = this;

        return telemetryDispatcher.recordStreamSample(playerAdapter);

    }

    /**
     *
     * @access public
     * @desc Stops authorization refresh listener, stops playback listener, and clears the payload value
     * @returns {Promise<Void>}
     *
     */
    release() {

        const { dustEnabled, logger, playerAdapter, telemetryDispatcher } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: PlaybackSession.name,
            urn: PlaybackSessionDustUrnReference.release
        });

        logger.info(PlaybackSession.name, 'Release.');

        return telemetryDispatcher.release(playerAdapter).then(() => {

            return playerAdapter.clean();

        }).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {SDK.Media.MediaItem} mediaItem
     * @desc if adEngine is enabled and adEngine data exists try to update adEngine cookies
     * @returns {Promise<Void>}
     *
     */
    updateAdEngineCookies(mediaItem) {

        const errorMessage = {
            method: 'PlaybackSession.updateAdEngineCookies(mediaItem)',
            mediaItem: () => {
                return `${this.method} needs SDK.Media.MediaItem instance`;
            }
        };

        instanceStrict(mediaItem, MediaItem, errorMessage.mediaItem, InvalidArgumentException);

        const { adEngineManager, playerAdapter } = this;
        const { payload = {}, descriptor = {} } = mediaItem;
        const { adTargeting } = descriptor;
        const { trackingInfo = {} } = payload;
        const { adEngine } = trackingInfo;

        if (!adEngineManager.disabled && Check.nonEmptyObject(adEngine)) {

            const { cdn, corigin, fguid: ssess } = adEngine;

            /**
             *
             * @note ssess is mapped from fguid - identifies the unique user
             *
             */
            const adEngineOptions = Object.assign({}, {
                cdn,
                corigin,
                ssess
            }, adTargeting);

            // stores adEngine data to be used with adEngine .ts segment requests by the nativePlayer - might be ignored
            playerAdapter.setAdEngineData({ ssess, ...adEngine });

            // getCookies also exists but since we don't need to return the Cookie[] to the caller it is not used
            return adEngineManager.updateCookies(adEngineOptions);

        } else {

            // reset to an empty object if adEngine data is disabled or not returned with the playlist response
            playerAdapter.setAdEngineData({});

        }

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @param {Array<String>} drms
     * @desc Creates SDK.Drm.DrmProvider instances based on the provided data set
     * @returns {Array<SDK.Drm.DrmProvider>}
     *
     */
    createDrmProviders(drms) {

        const { logger, drmManager, tokenManager, playerAdapter } = this;
        const hasAtvPlayerAdapter = Check.instanceStrict(playerAdapter, AtvPlayerAdapter);
        const providers = [];

        const providerReference = {
            [DrmType.FAIRPLAY]: hasAtvPlayerAdapter ? AtvFairPlayDrmProvider : FairPlayDrmProvider,
            [DrmType.PLAYREADY]: PlayReadyDrmProvider,
            [DrmType.WIDEVINE]: WidevineDrmProvider,
            [DrmType.SILK]: SilkDrmProvider
        };

        if (Check.nonEmptyArray(drms)) {

            for (const drm of drms) {

                const Provider = providerReference[drm];

                if (Check.assigned(Provider)) {

                    logger.info(PlaybackSession.name, `Created ${Provider.name} instance`);

                    providers.push(
                        new Provider(drmManager, tokenManager, logger)
                    );

                }

            }

        }

        return providers;

    }

    /**
     *
     * @access private
     * @param {Array<String>} drms
     * @desc Additional filtering for provided drms. This is especially useful for
     * testing, in certain cases the drms returned may not be supported by a given
     * platform, breaking the logic in the PlayerAdapter.
     * @returns {Array<String>}
     *
     */
    filterDrms(drms) {

        const { playerAdapter } = this;

        const hasAtvPlayerAdapter = Check.instanceStrict(playerAdapter, AtvPlayerAdapter);
        const hasBamWebPlayerAdapter = Check.instanceStrict(playerAdapter, BamWebPlayerAdapter);
        const hasTrilithiumPlayerAdapter = Check.instanceStrict(playerAdapter, TrilithiumPlayerAdapter);
        const hasChromecastPlayerAdapter = Check.instanceStrict(playerAdapter, ChromecastPlayerAdapter);
        const hasCafPlayerAdapter = Check.instanceStrict(playerAdapter, CafPlayerAdapter);
        const hasBamHlsPlayerAdapter = Check.instanceStrict(playerAdapter, BamHlsPlayerAdapter);

        const hasNeedForFilter = [
            hasAtvPlayerAdapter,
            hasBamWebPlayerAdapter,
            hasTrilithiumPlayerAdapter,
            hasChromecastPlayerAdapter,
            hasCafPlayerAdapter,
            hasBamHlsPlayerAdapter
        ];

        // Skip filter logic completely for PlayerAdapter(s) that dont need it
        if (!hasNeedForFilter.includes(true)) {
            return drms;
        }

        // Filter drms, return a new Array with supported drms for a particular PlayerAdapter
        return drms.filter((drm) => {

            if (hasAtvPlayerAdapter) {
                return drm !== DrmType.PLAYREADY && drm !== DrmType.WIDEVINE;
            }

            if (hasTrilithiumPlayerAdapter) {
                return drm !== DrmType.FAIRPLAY && drm !== DrmType.WIDEVINE;
            }

            if (hasChromecastPlayerAdapter || hasCafPlayerAdapter) {
                return drm !== DrmType.FAIRPLAY && drm !== DrmType.PLAYREADY;
            }

            if (hasBamWebPlayerAdapter || hasBamHlsPlayerAdapter) {
                return drm !== DrmType.SILK;
            }

            return drm;

        });

    }

    /**
     *
     * @access private
     * @param {Array<String>} drms
     * @desc Sets SDK.Drm.DrmProvider instances on a given PlayerAdapter
     * @returns {Promise<Void>}
     *
     */
    setDrmProviders(drms) {

        const { playerAdapter, priorityDrms, logger } = this;

        const hasDrms = Check.nonEmptyArray(drms);
        const hasDrmProvider = Check.assigned(playerAdapter.drmProvider);
        const hasDrmProviders = Check.nonEmptyArray(playerAdapter.drmProviders);
        const hasTrilithiumPlayerAdapter = Check.instanceStrict(playerAdapter, TrilithiumPlayerAdapter);

        if (hasDrmProvider || hasDrmProviders) {
            return Promise.resolve();
        }

        let providers;

        if (Check.not.assigned(priorityDrms)) {

            // Provide a default DrmProvider {SilkDrmProvider}
            providers = this.createDrmProviders(this.filterDrms([DrmType.SILK]));

            // Process the provided drms data set
            // create a DrmProvider instance for each DrmType
            if (hasDrms) {

                const currentDrms = hasTrilithiumPlayerAdapter ? drms.concat([DrmType.SILK]) : drms;
                const filteredDrms = this.filterDrms(currentDrms);

                providers = this.createDrmProviders(filteredDrms);

            }

        } else {

            if (hasDrms) {

                // Check drms vs priority drm
                for (const priorityDrm of priorityDrms) {

                    drms.find((drm) => {

                        if (drm === priorityDrm) {
                            return true;
                        }

                        logger.warn(PlaybackSession.name, `${priorityDrm} is not part of the supported DRMs.`);

                        return false;

                    });

                }

            }

            providers = this.createDrmProviders(priorityDrms);

        }

        // API based check, PlayerAdapter(s) that need a group
        // of DrmProvider(s) expose the drmProviders property and #setDrmProviders method
        if (!hasDrmProviders && Check.function(playerAdapter.setDrmProviders)) {
            return playerAdapter.setDrmProviders(providers);
        }

        // Standard check, assign a DrmProvider if one isn't
        // already associated with a given PlayerAdapter
        if (!hasDrmProvider) {
            return playerAdapter.setDrmProvider(providers[0]);
        }

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @param {PlaybackAttributes} playbackAttributes
     * @desc Bootstraps necessary DRM logic
     * @returns {Promise<Void>}
     *
     */
    setDrm(playbackAttributes) {

        const { logger, playerAdapter } = this;
        const { audioSegmentTypes, videoSegmentTypes, drms } = playbackAttributes;

        if (Check.function(playerAdapter.updateSegmentFormat)) {

            logger.info(PlaybackSession.name, 'Update segment types.');
            logger.info(PlaybackSession.name, `Audio segment types: ${JSON.stringify(audioSegmentTypes)}`);
            logger.info(PlaybackSession.name, `Video segment types: ${JSON.stringify(videoSegmentTypes)}`);

            return playerAdapter.updateSegmentFormat(audioSegmentTypes, videoSegmentTypes).then(() => {
                return this.setDrmProviders(drms);
            });

        }

        return this.setDrmProviders(drms);

    }

    /**
     *
     * @access private
     * @desc Updates the PlayerAdapter instance and ensures the
     * stored accessToken property is fresh
     * @returns {Promise<Void>}
     *
     */
    updatePlayerAdapter() {

        const { playerAdapter } = this;

        playerAdapter.accessToken = this.accessToken.token;

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @desc listens and reacts to {SDK.Token.TokenManager} events
     *
     */
    bindTokenProviderEvents() {

        const { playerAdapter, tokenManager, authCookieProvider } = this;

        tokenManager.on('TokenRefreshFailed', (exception) => {

            playerAdapter.onAccessFailed(exception);

        });

        tokenManager.on('AccessChange', (accessToken) => {

            const token = Util.getSafe(() => accessToken.token);

            playerAdapter.onAccessChanged(token);

            return authCookieProvider.createCookie(accessToken);

        });

    }

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.tokenManager.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/media/playbackSession.js